import { useEffect, useRef, useState } from 'react';
import type { RichTextConfig } from '@/core/models/richText';
import type { SendEmailDialogData } from '@/core/models/reports.models';
import { notifyBlobError, notifyError, notifySuccess } from '@/core/notify/notify';
import { EMAIL_PATTERN, validateForm } from '@/features/manage/formRules';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import './SendTrialBalance.scss';

/** Angular editor options of the send dialog, verbatim (`fonts`, `orderedList` and `unorderedList` are not valid ids). */
const EDITOR_CONFIG: RichTextConfig = {
  editable: true,
  spellcheck: true,
  height: '200',
  minHeight: '0',
  maxHeight: 'auto',
  width: 'auto',
  minWidth: '0',
  translate: 'yes',
  enableToolbar: true,
  showToolbar: true,
  placeholder: 'Enter text here...',
  defaultParagraphSeparator: '',
  sanitize: true,
  toolbarPosition: 'bottom',
  toolbarHiddenButtons: [
    [
      'fontName',
      'fontSize',
      'backgroundColor',
      'strikeThrough',
      'customClasses',
      'insertHorizontalRule',
      'removeFormat',
      'heading',
      'link',
      'unlink',
      'insertImage',
      'insertVideo',
      'fonts',
      'justifyLeft',
      'justifyCenter',
      'justifyRight',
      'justifyFull',
      'indent',
      'outdent',
      'orderedList',
      'unorderedList',
      'textColor',
    ],
  ],
};

/** Every address of a To / CC / BCC box (split on a comma or semicolon; blank entries are ignored) is a valid email address. */
export function addressesAreValid(text: string): boolean {
  return text
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .every((address) => EMAIL_PATTERN.test(address));
}

/**
 * "Compose and send an email with the report attached" (Angular SendTrialBalanceComponent), shared by the trial
 * balance report and the Annual/Management accounts lists. Closes with true after a successful send.
 */
export function SendTrialBalance({ data, dialogRef }: DialogComponentProps<SendEmailDialogData, boolean>) {
  const [title] = useState(data ? data.title : 'Send Trial Balance');
  const [attachmentName] = useState(data ? data.draft.attachmentName || 'Trial Balance' : 'Trial Balance');
  const [to, setTo] = useState(data ? data.draft.to : '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(data ? data.draft.subject : '');
  const [body, setBody] = useState(data ? data.draft.body : '');
  const [isSending, setIsSending] = useState(false);
  /** Send was pressed once: the mandatory boxes left empty are outlined in red. */
  const [tried, setTried] = useState(false);
  const sending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const downloadAttachment = async () => {
    if (!data) {
      return;
    }
    try {
      await data.downloadAttachment();
    } catch (error) {
      notifyBlobError(error, 'The attachment could not be downloaded.');
    }
  };

  const send = async () => {
    if (!data || sending.current) {
      return;
    }
    setTried(true);
    if (!validateForm(!!to.trim() && !!subject.trim())) {
      return;
    }
    if (![to, cc, bcc].every(addressesAreValid)) {
      notifyError('Enter valid email addresses, separated by commas.');
      return;
    }
    sending.current = true;
    setIsSending(true);
    try {
      const response = await data.send({
        to: to.trim(),
        ...(cc.trim() ? { cc: cc.trim() } : {}),
        ...(bcc.trim() ? { bcc: bcc.trim() } : {}),
        subject: subject.trim(),
        body: body ?? '',
      });
      // A bulk send where only some went closes too (nothing is sent twice), with the ones that did not as an error.
      if (response.status === 'Partial') {
        notifyError(response.message);
      } else {
        notifySuccess(response.message);
      }
      dialogRef.close(true);
    } catch (error) {
      notifyError(error);
    } finally {
      sending.current = false;
      if (mounted.current) {
        setIsSending(false);
      }
    }
  };

  return (
    <div className="mat-dialog-wrapper send-trial-balance">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{title}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="form-field">
              <div className="d-flex align-items-center justify-space-between w-510">
                <p className="fw-bold">
                  To <RequiredMark />
                </p>
                <span className="d-flex gap-10">
                  <a
                    href="#"
                    className="link text-underline-none"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowCc((shown) => !shown);
                    }}
                  >
                    CC
                  </a>
                  <a
                    href="#"
                    className="link text-underline-none"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowBcc((shown) => !shown);
                    }}
                  >
                    BCC
                  </a>
                </span>
              </div>
              <InputField
                fieldClassName="w-510"
                autoComplete="off"
                name="to"
                maxLength={1000}
                invalid={tried && (!to.trim() || !addressesAreValid(to))}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            {showCc && (
              <div className="form-field">
                <p className="fw-bold">CC</p>
                <InputField
                  fieldClassName="w-510"
                  autoComplete="off"
                  name="cc"
                  maxLength={1000}
                  invalid={tried && !addressesAreValid(cc)}
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                />
              </div>
            )}
            {showBcc && (
              <div className="form-field">
                <p className="fw-bold">BCC</p>
                <InputField
                  fieldClassName="w-510"
                  autoComplete="off"
                  name="bcc"
                  maxLength={1000}
                  invalid={tried && !addressesAreValid(bcc)}
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                />
              </div>
            )}
            <div className="form-field">
              <p className="fw-bold">
                Subject <RequiredMark />
              </p>
              <InputField
                fieldClassName="w-510"
                autoComplete="off"
                name="subject"
                maxLength={255}
                invalid={tried && !subject.trim()}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="form-field send-editor mb-1">
              <RichTextEditor id="editor1" value={body} onChange={setBody} config={EDITOR_CONFIG} />
            </div>
            <div className="mb-1 mt-1">
              <a
                href="#"
                className="link text-underline-none d-flex align-items-center"
                onClick={(event) => {
                  event.preventDefault();
                  void downloadAttachment();
                }}
              >
                <Icon name="attachment" outlined className="rotate font-size-16" />
                {attachmentName}
              </a>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSending} onClick={() => void send()}>
              Send
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
